import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Divider,
  useTheme,
} from '@mui/material';
import {
  AccessTime,
  LocationOn,
  CalendarToday,
  Timer,
  Warning,
} from '@mui/icons-material';
import GlassCard from '../shared/GlassCard';
import EmptyState from '../shared/EmptyState';
import SessionTicker from '../shared/SessionTicker';
import api from '../../utils/api';

/**
 * SessionClock
 * Displays elapsed session time using SessionTicker (RAF-based, second-gated).
 * Also fetches remaining daily limit from /sessions/remaining-time (same as original).
 * Inline timing logic removed — SessionTicker owns it.
 */
function SessionClock({ session }) {
  const theme = useTheme();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [timeInfo, setTimeInfo] = useState(null);

  // Live clock — updates each minute only (display doesn't need per-second)
  useEffect(() => {
    const id = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  // Fetch remaining time for hours-based plans (same endpoint / payload as original)
  useEffect(() => {
    if (!session) return;

    const fetchRemainingTime = async () => {
      try {
        const response = await api.get('/sessions/remaining-time');
        if (response.data.data) setTimeInfo(response.data.data);
      } catch {
        // Non-critical — hide the section silently
      }
    };

    fetchRemainingTime();
    const id = setInterval(fetchRemainingTime, 30000);
    return () => clearInterval(id);
  }, [session]);

  const formatTime = (date) =>
    date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

  const formatDate = (date) =>
    date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const formatRemainingTime = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  // Map remaining time to a theme palette color (no hardcoded hexes)
  const getRemainingTone = (minutes) => {
    if (minutes <= 5) return theme.palette.error.main;
    if (minutes <= 15) return theme.palette.warning.main;
    if (minutes <= 30) return theme.palette.warning.light ?? theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const getRemainingProgress = () => {
    if (!timeInfo || timeInfo.unlimited) return 100;
    return Math.max(0, (timeInfo.remainingMinutes / timeInfo.allowedMinutes) * 100);
  };

  if (!session) {
    return (
      <EmptyState
        icon={<AccessTime />}
        title="No active session"
        description="Check in at a library to start tracking your session time."
      />
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {/* Primary elapsed display */}
      <GlassCard sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="overline" color="primary.main" fontWeight={700} display="block" mb={1}>
          Time in Session
        </Typography>

        <Typography
          variant="h3"
          fontWeight={800}
          sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.primary', mb: 0.5 }}
        >
          <SessionTicker enteredAt={session.entered_at} showSeconds />
        </Typography>

        <Typography variant="caption" color="text.secondary">
          Checked in at {new Date(session.entered_at).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          })}
        </Typography>
      </GlassCard>

      {/* Session details */}
      <GlassCard sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700} color="text.primary" mb={2}>
          Session Details
        </Typography>

        <Box display="flex" flexDirection="column" gap={2}>
          {/* Library */}
          <Box display="flex" gap={2} alignItems="center">
            <Box
              sx={{
                width: 40, height: 40, borderRadius: 2,
                bgcolor: 'primary.main', display: 'flex',
                alignItems: 'center', justifyContent: 'center', color: 'white',
              }}
            >
              <LocationOn />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Library</Typography>
              <Typography variant="body1" fontWeight={600} color="text.primary">
                {session.library?.name ?? 'Unknown Library'}
              </Typography>
            </Box>
          </Box>

          <Divider />

          {/* Check-in time */}
          <Box display="flex" gap={2} alignItems="center">
            <Box
              sx={{
                width: 40, height: 40, borderRadius: 2,
                bgcolor: 'success.main', display: 'flex',
                alignItems: 'center', justifyContent: 'center', color: 'white',
              }}
            >
              <AccessTime />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Checked In</Typography>
              <Typography variant="body1" fontWeight={600} color="text.primary">
                {formatTime(new Date(session.entered_at))}
              </Typography>
            </Box>
          </Box>

          <Divider />

          {/* Date */}
          <Box display="flex" gap={2} alignItems="center">
            <Box
              sx={{
                width: 40, height: 40, borderRadius: 2,
                bgcolor: 'info.main', display: 'flex',
                alignItems: 'center', justifyContent: 'center', color: 'white',
              }}
            >
              <CalendarToday />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Date</Typography>
              <Typography variant="body1" fontWeight={600} color="text.primary">
                {formatDate(new Date(session.entered_at))}
              </Typography>
            </Box>
          </Box>
        </Box>
      </GlassCard>

      {/* Remaining daily limit */}
      {timeInfo && !timeInfo.unlimited && timeInfo.remainingMinutes !== undefined && (
        <GlassCard
          sx={{
            p: 3,
            border: timeInfo.remainingMinutes <= 15
              ? `2px solid ${getRemainingTone(timeInfo.remainingMinutes)}`
              : undefined,
          }}
        >
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Box
              sx={{
                width: 40, height: 40, borderRadius: 2,
                bgcolor: getRemainingTone(timeInfo.remainingMinutes),
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
              }}
            >
              {timeInfo.remainingMinutes <= 15 ? <Warning /> : <Timer />}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Today's Limit ({timeInfo.hoursPerDay}h/day)
              </Typography>
              <Typography variant="body1" fontWeight={700} sx={{ color: getRemainingTone(timeInfo.remainingMinutes) }}>
                {timeInfo.remainingMinutes > 0
                  ? `${formatRemainingTime(timeInfo.remainingMinutes)} remaining`
                  : 'Limit reached'}
              </Typography>
            </Box>
          </Box>

          {/* Progress bar using theme tokens */}
          <Box sx={{ height: 8, borderRadius: 999, bgcolor: 'background.subtle', overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%',
                width: `${getRemainingProgress()}%`,
                bgcolor: getRemainingTone(timeInfo.remainingMinutes),
                borderRadius: 999,
                transition: 'width 0.4s ease',
              }}
            />
          </Box>

          <Box display="flex" justifyContent="space-between" mt={1}>
            <Typography variant="caption" color="text.secondary">
              Used: {formatRemainingTime(timeInfo.usedMinutes ?? 0)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total: {timeInfo.hoursPerDay}h
            </Typography>
          </Box>
        </GlassCard>
      )}

      {/* Current wall-clock time */}
      <GlassCard sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
          Current Time
        </Typography>
        <Typography variant="h5" fontWeight={700} color="text.primary">
          {formatTime(currentTime)}
        </Typography>
      </GlassCard>
    </Box>
  );
}

export default SessionClock;
